import Phaser from 'phaser';
import { Entity } from '../entities/base/Entity';

/**
 * Bounding box normalizado (valores 0-1 relativos al sprite)
 */
export interface BoundingBox {
  widthRatio: number;
  heightRatio: number;
  offsetXRatio: number;
  offsetYRatio: number;
}

/**
 * Animation collision data with per-frame hitboxes
 */
export interface AnimationCollision {
  name: string;
  frameCount: number;
  speed: number;
  isLooping: boolean;
  frames: BoundingBox[];
}

/**
 * Datos de colisión de una entidad
 */
export interface EntityCollision {
  name: string;
  pluginId: string;
  baseWidth: number;
  baseHeight: number;
  originX: number;
  originY: number;
  defaultHitbox: BoundingBox;
  // Note: isSolid removed - SSOT for solid behavior is now in layouts-c3data.json and entities-overrides.json
  animations?: AnimationCollision[];
  imagePoints?: Array<{ name: string; x: number; y: number }>;
}

/**
 * Estructura del JSON de colisiones
 */
export interface CollisionData {
  version: string;
  generatedAt: string;
  entities: Record<string, EntityCollision>;
  collisionObjects: string[];
}

/**
 * DataManagerCollision - Gestiona los datos de colisión extraídos de C3
 *
 * Carga collision-c3data.json (base) y collision-overrides.json (ajustes manuales)
 * y hace merge en runtime. Provee métodos para:
 * - Obtener hitbox de una entidad por nombre
 * - Aplicar hitbox a un sprite de Phaser
 */
export class DataManagerCollision {
  private scene: Phaser.Scene;
  private data: CollisionData | null = null;
  private loaded: boolean = false;

  // Cache keys
  static readonly C3DATA_KEY = 'collision-c3data';
  static readonly OVERRIDES_KEY = 'collision-overrides';

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Precargar los JSONs de colisiones (c3data + overrides)
   */
  preload(): void {
    this.scene.load.json(DataManagerCollision.C3DATA_KEY, '/assets/data/collision-c3data.json');
    this.scene.load.json(DataManagerCollision.OVERRIDES_KEY, '/assets/data/collision-overrides.json');
  }

  /**
   * Cargar datos desde cache y hacer merge de c3data + overrides
   */
  loadFromCache(c3dataKey: string = DataManagerCollision.C3DATA_KEY): void {
    const c3data = this.scene.cache.json.get(c3dataKey) as CollisionData | undefined;
    if (!c3data) {
      console.error(`[DataManagerCollision] No c3data found in cache for key: ${c3dataKey}`);
      return;
    }

    // Clone c3data as base
    this.data = JSON.parse(JSON.stringify(c3data)) as CollisionData;

    // Load and merge overrides
    this.loadOverrides();

    this.loaded = true;
  }

  /**
   * Load and merge overrides into the base data
   */
  loadOverrides(key: string = DataManagerCollision.OVERRIDES_KEY): void {
    if (!this.data) {
      console.error('[DataManagerCollision] Cannot load overrides: base data not loaded');
      return;
    }

    const overrides = this.scene.cache.json.get(key) as { entities?: Record<string, Partial<EntityCollision>> } | undefined;
    if (!overrides?.entities) {
      // No overrides, that's fine
      return;
    }

    // Merge each entity override
    for (const [entityName, override] of Object.entries(overrides.entities)) {
      if (this.data.entities[entityName]) {
        // Merge override into existing entity
        this.deepMerge(this.data.entities[entityName], override);
      } else {
        // New entity from overrides (must have all required fields)
        if (this.isCompleteEntityCollision(override)) {
          this.data.entities[entityName] = override as EntityCollision;
        } else {
          console.warn(`[DataManagerCollision] Override for "${entityName}" missing required fields, skipping`);
        }
      }
    }
  }

  /**
   * Check if override has all required EntityCollision fields
   */
  private isCompleteEntityCollision(data: Partial<EntityCollision>): data is EntityCollision {
    return (
      typeof data.name === 'string' &&
      typeof data.pluginId === 'string' &&
      typeof data.baseWidth === 'number' &&
      typeof data.baseHeight === 'number' &&
      typeof data.originX === 'number' &&
      typeof data.originY === 'number' &&
      data.defaultHitbox !== undefined
    );
  }

  /**
   * Deep merge source into target
   */
  private deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
    for (const key in source) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        target[key] = this.deepMerge(
          (targetValue as Record<string, unknown>) || {},
          sourceValue as Record<string, unknown>
        ) as T[Extract<keyof T, string>];
      } else if (sourceValue !== undefined) {
        target[key] = sourceValue as T[Extract<keyof T, string>];
      }
    }
    return target;
  }

  /**
   * Verifica si los datos están cargados
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Obtiene los datos de colisión de una entidad
   */
  getCollision(entityName: string): EntityCollision | undefined {
    if (!this.data) return undefined;
    return Entity.findInObject(this.data.entities, entityName);
  }

  /**
   * Get hitbox for specific animation and frame
   */
  getFrameHitbox(
    entityName: string,
    animationName: string,
    frameIndex: number
  ): BoundingBox | undefined {
    const collision = this.getCollision(entityName);
    if (!collision?.animations) return collision?.defaultHitbox;

    const anim = collision.animations.find(a => Entity.matches(a.name, animationName));

    if (!anim?.frames) return collision.defaultHitbox;

    // Clamp frameIndex to valid range
    const index = Math.min(frameIndex, anim.frames.length - 1);
    return anim.frames[index] ?? collision.defaultHitbox;
  }

  /**
   * Get animation metadata
   */
  getAnimationData(entityName: string, animationName: string): AnimationCollision | undefined {
    const collision = this.getCollision(entityName);
    return collision?.animations?.find(a => Entity.matches(a.name, animationName));
  }

  /**
   * Aplica hitbox y origin a un sprite de Phaser
   *
   * WARNING: Por defecto useGroundedOrigin=true, lo que SOBRESCRIBE el origin a (0.5, 1.0).
   * Si la entidad ya tiene origin configurado por EntityFactory (desde SSOT), DEBES pasar:
   *   - skipOrigin: true (si ya se configuró externamente)
   *   - useGroundedOrigin: false (para structures que no caminan)
   *
   * Ejemplo correcto para spawns dinámicos (TransformBehavior, etc.):
   * ```typescript
   * const hasOriginOverride = entity.config.originY !== undefined;
   * collisionManager.applyHitbox(entity, entityId, {
   *   useGroundedOrigin: false,
   *   skipOrigin: hasOriginOverride,
   * });
   * ```
   *
   * @param sprite - El sprite al que aplicar el hitbox
   * @param entityName - Nombre de la entidad en collision-c3data.json
   * @param options - Opciones de configuración
   */
  applyHitbox(
    sprite: Phaser.Physics.Arcade.Sprite,
    entityName: string,
    options: {
      /** Si true, usa origin (0.5, 1.0) = centro-abajo (para units que caminan) */
      useGroundedOrigin?: boolean;
      /** Si true, no modifica el origin del sprite (ya fue establecido externamente) */
      skipOrigin?: boolean;
      /** Ratio del hitbox respecto al sprite (0-1), default 1.0 = full size */
      hitboxWidthRatio?: number;
      hitboxHeightRatio?: number;
    } = {}
  ): boolean {
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    if (!body) {
      console.warn(`[DataManagerCollision] Sprite has no physics body: ${entityName}`);
      return false;
    }

    const collision = this.getCollision(entityName);

    // Set origin: MUST specify skipOrigin OR useGroundedOrigin explicitly
    if (!options.skipOrigin) {
      // ERROR if caller didn't explicitly specify origin behavior
      if (options.useGroundedOrigin === undefined) {
        console.error(
          `[DataManagerCollision] applyHitbox("${entityName}") called without explicit origin options. ` +
          `MUST pass { skipOrigin: true } if origin was set externally, ` +
          `or { useGroundedOrigin: true/false } to set origin here.`
        );
        return false;
      }
      if (options.useGroundedOrigin) {
        sprite.setOrigin(0.5, 1.0); // Center-bottom (feet)
      } else if (collision) {
        sprite.setOrigin(collision.originX, collision.originY);
      }
    }

    // Usar frame size (sin escala). Mantiene compatibilidad con ratios extraídos de C3.
    const frame = sprite.frame;
    const frameWidth = frame.width;
    const frameHeight = frame.height;

    // Get hitbox data from collision data, or use defaults
    const hitbox = collision?.defaultHitbox;

    // Hitbox ratios: prefer options, then collision data, then full size
    let widthRatio: number;
    let heightRatio: number;
    if (options.hitboxWidthRatio !== undefined) {
      widthRatio = options.hitboxWidthRatio;
    } else if (hitbox?.widthRatio !== undefined) {
      widthRatio = hitbox.widthRatio;
    } else {
      console.warn(`[DataManagerCollision] No hitbox widthRatio for: ${entityName}, using 1.0`);
      widthRatio = 1.0;
    }
    if (options.hitboxHeightRatio !== undefined) {
      heightRatio = options.hitboxHeightRatio;
    } else if (hitbox?.heightRatio !== undefined) {
      heightRatio = hitbox.heightRatio;
    } else {
      console.warn(`[DataManagerCollision] No hitbox heightRatio for: ${entityName}, using 1.0`);
      heightRatio = 1.0;
    }

    // Calculate hitbox in frame coordinates
    const hitboxWidth = frameWidth * widthRatio;
    const hitboxHeight = frameHeight * heightRatio;

    // Offset ratios: use collision data if available, otherwise calculate
    let offsetX: number;
    let offsetY: number;

    if (hitbox?.offsetXRatio !== undefined && hitbox?.offsetYRatio !== undefined) {
      // Use offsets from collision data (extracted from C3)
      offsetX = frameWidth * hitbox.offsetXRatio;
      offsetY = frameHeight * hitbox.offsetYRatio;
    } else {
      // Fallback: center horizontally, align to bottom (feet)
      console.warn(`[DataManagerCollision] Missing hitbox offset for: ${entityName}, using calculated fallback`);
      offsetX = (frameWidth - hitboxWidth) / 2;
      offsetY = frameHeight - hitboxHeight;
    }

    // Apply size and offset (in display coordinates)
    body.setSize(hitboxWidth, hitboxHeight);
    body.setOffset(offsetX, offsetY);

    return collision !== undefined;
  }

  /**
   * Obtiene los image points de una entidad (para disparos, etc.)
   */
  getImagePoints(entityName: string): Array<{ name: string; x: number; y: number }> | undefined {
    const collision = this.getCollision(entityName);
    return collision?.imagePoints;
  }

  // Note: hasSolidBehavior() and isSolid() removed
  // SSOT for solid behavior is now:
  // - TileSprites: layouts-c3data.json (behaviors.solid)
  // - Entities: entities-overrides.json (behaviors.solid)

  /**
   * Obtiene lista de collision objects (cajas invisibles del nivel)
   */
  getCollisionObjects(): string[] {
    if (!this.data) {
      console.error('[DataManagerCollision] getCollisionObjects() called before data loaded');
      return [];
    }
    return this.data.collisionObjects;
  }

  /**
   * Debug: muestra info de una entidad
   */
  debugEntity(entityName: string): void {
    const collision = this.getCollision(entityName);
    if (!collision) {
      console.log(`[DataManagerCollision] No data for: ${entityName}`);
      return;
    }

    console.log(`[DataManagerCollision] ${entityName}:`);
    console.log(`  Base size: ${collision.baseWidth}x${collision.baseHeight}`);
    console.log(`  Origin: (${collision.originX.toFixed(2)}, ${collision.originY.toFixed(2)})`);
    console.log(`  Hitbox: ${(collision.defaultHitbox.widthRatio * 100).toFixed(0)}% x ${(collision.defaultHitbox.heightRatio * 100).toFixed(0)}%`);
    console.log(`  Offset: (${(collision.defaultHitbox.offsetXRatio * 100).toFixed(0)}%, ${(collision.defaultHitbox.offsetYRatio * 100).toFixed(0)}%)`);
    // Note: isSolid removed - SSOT for solid is now in layouts-c3data/entities-overrides
    if (collision.animations) {
      console.log(`  Animations: ${collision.animations.map(a => a.name).join(', ')}`);
    }
  }
}
